/**
 * V70-S-4 — 근로 현황(조직·전사) 직원 상세 팝업의 일일 행 (화면과 엑셀이 같은 함수를 쓴다)
 *
 * 행 = 출퇴근 기록(attendances) 날짜 ∪ 승인 근무가 있는 날짜 (날짜 오름차순)
 *  - 출근·퇴근·근무시간·지각·상태: 기존 출퇴근 기록 그대로 (기존 판정 로직 변경 없음)
 *  - 연장·야간·휴일·휴일 야간: 서버(getMyAttendances includeApprovedWork)가 승인 근무 신청으로 계산한 값
 *    (연장 근무 관리와 같은 V70-S-2 상호 배타 분류. 출퇴근 근무시간과 별개이며 서로 덮어쓰지 않는다)
 *  - V70-S-5: 숫자 표시는 포함 기준(연장 = 연장 + 야간, 휴일 = 휴일 + 휴일 야간, toInclusiveWorkDisplay).
 *    상태 배지는 원본 분류 기준 그대로 (야간만 있는 날에 '연장' 배지를 붙이지 않는다)
 */
import { INCLUSIVE_WORK_NOTE, toInclusiveWorkDisplay } from '../../../shared/utils/approvedWorkDisplay';

export interface DailyAttendance {
  id: string;
  date: string;
  checkInLocalTime?: string | null;
  checkOutLocalTime?: string | null;
  checkInTime?: any;
  checkOutTime?: any;
  isLate?: boolean;
  isHolidayWork?: boolean;
  holidayWorkType?: 'weekend' | 'holiday';
  actualWorkMinutes?: number;
  approvalRequestId?: string;
}

export interface ApprovedWorkDay {
  date: string;
  overtimeMinutes: number;
  nightMinutes: number;
  holidayMinutes: number;
  holidayNightMinutes: number;
  exceptionMinutes: number;
  requestCount: number;
}

export type ApprovedWorkResponse =
  | { policy: 'KR'; period: { startDate: string; endDate: string }; days: ApprovedWorkDay[]; totals: Record<string, number> }
  | { policy: 'NOT_APPLICABLE'; reason: string };

export interface DailyRow {
  key: string;
  date: string;
  dayLabel: string;
  checkIn: string;
  checkOut: string;
  workTime: string;
  late: boolean;
  /** 아래 4개는 표시값(V70-S-5 포함 기준). 원본 분류가 아님 */
  overtimeMinutes: number | null;
  nightMinutes: number | null;
  holidayMinutes: number | null;
  holidayNightMinutes: number | null;
  status: { text: string; color: string };
  /** 승인 근무 유형 배지 (짧은 표기) */
  badges: string[];
}

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export function getDayOfWeekLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return DAY_LABELS[new Date(y, m - 1, d).getDay()] || '-';
}

/** 기존 근무시간 표시 규칙 그대로 (0·없음 → '-') */
export function formatMinutesToHours(min?: number | null): string {
  if (typeof min !== 'number' || !Number.isFinite(min)) return '-';
  if (min <= 0) return '-';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return `${h}시간`;
  return `${h}시간 ${m}분`;
}

/** 기존 상태 라벨 (V66 로직 그대로) */
export function getStatusLabel(att: DailyAttendance | null): { text: string; color: string } {
  if (!att) return { text: '기록 없음', color: 'var(--color-gray-400)' };
  if (att.isHolidayWork) {
    return { text: att.holidayWorkType === 'holiday' ? '휴일근무' : '주말근무', color: 'var(--color-blue-600)' };
  }
  const hasIn = !!att.checkInLocalTime || !!att.checkInTime;
  const hasOut = !!att.checkOutLocalTime || !!att.checkOutTime;
  if (!hasIn && !hasOut) return { text: '기록 없음', color: 'var(--color-gray-400)' };
  if (!hasIn) return { text: '미출근', color: 'var(--color-red-600)' };
  if (!hasOut) return { text: '미퇴근', color: 'var(--color-orange-600)' };
  return { text: '정상', color: 'var(--color-gray-700)' };
}

export const WORK_BADGES: Array<{ key: keyof Pick<ApprovedWorkDay, 'overtimeMinutes' | 'nightMinutes' | 'holidayMinutes' | 'holidayNightMinutes'>; label: string }> = [
  { key: 'overtimeMinutes', label: '연장' },
  { key: 'nightMinutes', label: '야간' },
  { key: 'holidayMinutes', label: '휴일' },
  { key: 'holidayNightMinutes', label: '휴일야간' },
];

export function buildDailyRows(attendances: DailyAttendance[], approvedWork: ApprovedWorkResponse | null | undefined): DailyRow[] {
  const applicable = approvedWork?.policy === 'KR';
  const workByDate = new Map<string, ApprovedWorkDay>(applicable ? (approvedWork as any).days.map((d: ApprovedWorkDay) => [d.date, d]) : []);
  const attByDate = new Map<string, DailyAttendance>();
  for (const a of attendances) if (!attByDate.has(a.date)) attByDate.set(a.date, a);
  const dates = Array.from(new Set(attendances.map((a) => a.date).concat(Array.from(workByDate.keys())))).sort();
  return dates.map((date) => {
    const att = attByDate.get(date) || null;
    const w = workByDate.get(date);
    // 배지: 원본(상호 배타) 분류 기준
    const badges = w ? WORK_BADGES.filter((b) => w[b.key] > 0).map((b) => b.label) : [];
    if (w && w.exceptionMinutes > 0) badges.push('예외');
    // 숫자: 원본 → 표시(포함 기준) 한 번만 변환. 분류 미적용(CN) = null
    const d = applicable ? toInclusiveWorkDisplay(w || { overtimeMinutes: 0, nightMinutes: 0, holidayMinutes: 0, holidayNightMinutes: 0 }) : null;
    return {
      key: att?.id || `work-${date}`,
      date,
      dayLabel: getDayOfWeekLabel(date),
      checkIn: att?.checkInLocalTime || '-',
      checkOut: att?.checkOutLocalTime || '-',
      workTime: formatMinutesToHours(att?.actualWorkMinutes),
      late: !!att?.isLate,
      overtimeMinutes: d ? d.overtime : null,
      nightMinutes: d ? d.night : null,
      holidayMinutes: d ? d.holiday : null,
      holidayNightMinutes: d ? d.holidayNight : null,
      status: getStatusLabel(att),
      badges,
    };
  });
}

/** 화면 표 컬럼 = 엑셀 컬럼 (순서 동일) */
export const DAILY_COLUMNS = ['날짜', '요일', '출근', '퇴근', '근무시간', '지각', '연장근무', '야간근무', '휴일근무', '휴일 야간근무', '상태'] as const;

/** 4개 근무시간 셀 표시 (분류 미적용(CN) = '해당 없음') */
export function formatApprovedCell(min: number | null): string {
  return min === null ? '해당 없음' : formatMinutesToHours(min);
}

/** 상태 셀 텍스트 (엑셀용: 상태 + 배지) — 화면은 상태 텍스트 + 배지로 같은 정보를 표시 */
export function statusCellText(r: DailyRow): string {
  return r.badges.length ? `${r.status.text} (${r.badges.join('·')})` : r.status.text;
}

/** 'YYYY-MM-DD' → 엑셀 날짜 일련번호 (시간대 영향 없음) */
export function excelDateSerial(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return (Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000;
}

/** 엑셀 데이터 행 — 날짜는 일련번호(서식 yyyy-mm-dd 는 시트 생성 시 지정), 나머지는 화면과 같은 값 */
export function deriveDailyExportRow(r: DailyRow): Array<string | number> {
  return [
    excelDateSerial(r.date),
    r.dayLabel,
    r.checkIn,
    r.checkOut,
    r.workTime,
    r.late ? '지각' : '-',
    formatApprovedCell(r.overtimeMinutes),
    formatApprovedCell(r.nightMinutes),
    formatApprovedCell(r.holidayMinutes),
    formatApprovedCell(r.holidayNightMinutes),
    statusCellText(r),
  ];
}

/** 표 머리글 위 안내 행 수 (직원·조회 월·기준·빈 줄) */
export const DAILY_EXPORT_HEADER_ROW = 5;

/**
 * 직원 1명 · 1개월 일일 근무 기록 시트
 *  1행 직원, 2행 조회 월(집계 기간), 3행 승인 근무 기준 안내, 4행 빈 줄, 5행 표 머리글, 6행부터 날짜 오름차순 데이터
 */
export function buildDailyRecordSheet(
  XLSX: any,
  rows: DailyRow[],
  meta: { employeeName: string | null; month: string; period?: { startDate: string; endDate: string } | null; notApplicableReason?: string | null },
) {
  const aoa: Array<Array<string | number>> = [
    ['직원', meta.employeeName || '-'],
    ['조회 월', meta.period ? `${meta.month} (${meta.period.startDate} ~ ${meta.period.endDate})` : meta.month],
    ['기준', meta.notApplicableReason
      ? `연장·야간·휴일·휴일 야간: ${meta.notApplicableReason}`
      : `연장·야간·휴일·휴일 야간 = 승인된 근무 신청 시간(자정·월 경계 분할). ${INCLUSIVE_WORK_NOTE} 근무시간 = 출퇴근 기록 기준`],
    [],
    [...DAILY_COLUMNS],
    ...rows.map(deriveDailyExportRow),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // 날짜 셀 서식
  rows.forEach((_, i) => {
    const ref = XLSX.utils.encode_cell({ r: DAILY_EXPORT_HEADER_ROW + i, c: 0 });
    if (ws[ref]) { ws[ref].t = 'n'; ws[ref].z = 'yyyy-mm-dd'; }
  });
  ws['!cols'] = [12, 5, 7, 7, 12, 6, 12, 12, 12, 14, 24].map((wch) => ({ wch }));
  return ws;
}

/** 파일명에 쓸 수 없는 문자 제거 */
export function dailyExportFileName(name: string | null, month: string): string {
  const safe = (name || '직원').replace(/[\\/:*?"<>|]/g, '').trim() || '직원';
  return `직원근무기록_${safe}_${month}.xlsx`;
}
